import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, IconButton, TextField, Checkbox, FormControlLabel, InputAdornment, Tooltip } from '@mui/material'
import { LevelType, AdditionalReadingCategory } from '@/types/api'
import ClearIcon from '@mui/icons-material/Clear'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DescriptionIcon from '@mui/icons-material/Description'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { useAppStore } from '@/store/appStore'
import { useContentBundle } from '@/hooks/useContentBundle'
import { ContentTree } from './ContentTree'
import { CrosswalksMenu } from './CrosswalksMenu'

const NAV_ROUTES: Record<string, string> = {
  overview: '/overview',
  appendices: '/appendices',
  glossary: '/glossary',
  'release-notes': '/release-notes',
  faq: '/faq',
}

function getNavKeyFromPath(pathname: string): string | null {
  for (const [key, route] of Object.entries(NAV_ROUTES)) {
    if (pathname === route || pathname.startsWith(route + '/')) {
      return key
    }
  }
  return null
}

interface SidebarContentProps {
  /**
   * 'desktop' renders inside the persistent <aside>; 'drawer' renders inside the
   * temporary mobile Drawer, where collapsing is meaningless and a selection
   * should dismiss the drawer.
   */
  variant: 'desktop' | 'drawer'
}

/**
 * The sidebar body, with no assumptions about the shell that hosts it.
 * Rendered as-is by Sidebar (desktop <aside>) and by MobileNavDrawer (MUI Drawer).
 * Returns a fragment so its children stay direct children of `.sidebar`, which
 * the `.sidebar--collapsed > :not(.sidebar-expand-btn)` rule depends on.
 */
export function SidebarContent({ variant }: SidebarContentProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isDrawer = variant === 'drawer'
  const {
    activeSidebarNav,
    setActiveSidebarNav,
    treeFilterText: debouncedFilterText,
    setTreeFilterText,
    expandAllPracticeAreas,
    collapseAllPracticeAreas,
    toggleSidebarCollapsed,
    sidebarCollapsed,
    setMobileNavOpen,
    selectedLevelId,
    levelFilterExact,
    setLevelFilterExact,
    isOffline,
  } = useAppStore()
  const { data: bundle } = useContentBundle()
  // Off: legacy document-collection popover. On: Crosswalks additional-reading overlay.
  const crosswalksAsReading = bundle?.crossWalksEnabled ?? false
  const crosswalksCollection = bundle?.documentCollections?.find(c => c.tag === 'crosswalks')
  const showCrosswalks = crosswalksAsReading
    ? bundle?.additionalReading?.some((r) => r.category === AdditionalReadingCategory.Crosswalks) ?? false
    : crosswalksCollection !== undefined

  // Draft-preview highlight: flag Overview/Appendices/Crosswalks nav links whose additional-reading
  // items include a Draft/InReview overlay. isDraft is always false outside draft preview mode.
  const overviewHasDraft = bundle?.additionalReading?.some((r) => r.category === AdditionalReadingCategory.Overview && r.isDraft) ?? false
  const appendicesHasDraft = bundle?.additionalReading?.some((r) => r.category === AdditionalReadingCategory.Appendices && r.isDraft) ?? false
  const crosswalksHasDraft = bundle?.additionalReading?.some((r) => r.category === AdditionalReadingCategory.Crosswalks && r.isDraft) ?? false

  // Sync active sidebar nav from current route
  useEffect(() => {
    const navKey = getNavKeyFromPath(location.pathname)
    if (navKey && navKey !== activeSidebarNav) {
      setActiveSidebarNav(navKey)
    }
  }, [location.pathname])

  const [navCollapsed, setNavCollapsed] = useState(false)
  const [crosswalksOpen, setCrosswalksOpen] = useState(false)
  const crosswalksAnchorRef = useRef<HTMLDivElement>(null)

  const navItems = [
    { key: 'overview', label: t('sidebar.overview'), icon: <DescriptionIcon fontSize="small" />, href: '/overview' },
    { key: 'appendices', label: t('sidebar.appendices'), icon: <LibraryBooksIcon fontSize="small" />, href: '/appendices' },
    { key: 'glossary', label: t('sidebar.glossary'), icon: <MenuBookIcon fontSize="small" />, href: '/glossary' },
    { key: 'user-content', label: t('sidebar.userContent'), icon: <DashboardCustomizeIcon fontSize="small" />, href: '/user-content', dataTour: 'sidebar-user-content' },
    ...(showCrosswalks
      ? [{ key: 'crosswalks', label: t('sidebar.crosswalks'), icon: <CompareArrowsIcon fontSize="small" />, href: '/crosswalks' } as const]
      : []),
  ] as const

  // Local state drives the input immediately; the store is updated after a short delay.
  const [filterInputValue, setFilterInputValue] = useState(debouncedFilterText)

  useEffect(() => {
    const timer = setTimeout(() => setTreeFilterText(filterInputValue), 300)
    return () => clearTimeout(timer)
  }, [filterInputValue])

  // Keep local state in sync if the store value is cleared externally (e.g. clear button).
  useEffect(() => {
    if (debouncedFilterText === '') setFilterInputValue('')
  }, [debouncedFilterText])

  const { setOverlayPage } = useAppStore()

  type OverlayType = 'overview' | 'appendices' | 'glossary' | 'user-content' | 'crosswalks'
  const OVERLAY_KEYS = new Set<OverlayType>(['overview', 'appendices', 'glossary', 'user-content', 'crosswalks'])

  const handleNavClick = (key: string, href: string) => {
    if (key === 'crosswalks' && !crosswalksAsReading) {
      if (isOffline) return
      // The crosswalks popup anchors to its nav row, which is inside the drawer,
      // so the drawer must stay open for it.
      setCrosswalksOpen((o) => !o)
      return
    }
    if (OVERLAY_KEYS.has(key as OverlayType)) {
      setOverlayPage({ type: key as OverlayType })
    } else {
      setActiveSidebarNav(key)
      navigate(href)
    }
    if (isDrawer) setMobileNavOpen(false)
  }

  return (
    <>
      <div className={`sidebar-nav-wrapper${navCollapsed ? ' sidebar-nav-wrapper--collapsed' : ''}`}>
        <button
          className="sidebar-nav-toggle"
          onClick={() => setNavCollapsed((c) => !c)}
          aria-expanded={!navCollapsed}
          aria-controls="sidebar-nav-items"
          title={navCollapsed ? t('sidebar.showNavLinks') : t('sidebar.hideNavLinks')}
        >
          <span>{t('sidebar.navigation')}</span>
          {navCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </button>
        <nav id="sidebar-nav-items" className="sidebar-nav" aria-label={t('sidebar.supplementaryNavigation')}>
          {navItems.map((item) => {
            const isCrosswalksMenu = item.key === 'crosswalks' && !crosswalksAsReading
            const isCrosswalksOffline = isCrosswalksMenu && isOffline
            const navDiv = (
              <div
                key={item.key}
                ref={isCrosswalksMenu ? crosswalksAnchorRef : undefined}
                className={`sidebar-nav-item${activeSidebarNav === item.key ? ' active' : ''}${isCrosswalksMenu && crosswalksOpen ? ' active' : ''}`}
                role="button"
                tabIndex={isCrosswalksOffline ? -1 : 0}
                onClick={() => handleNavClick(item.key, item.href)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleNavClick(item.key, item.href)
                }}
                aria-disabled={isCrosswalksOffline || undefined}
                aria-current={activeSidebarNav === item.key ? 'page' : undefined}
                aria-expanded={isCrosswalksMenu ? crosswalksOpen : undefined}
                aria-haspopup={isCrosswalksMenu ? 'listbox' : undefined}
                style={isCrosswalksOffline ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                {...('dataTour' in item && item.dataTour ? { 'data-tour': item.dataTour } : {})}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                {item.label}
                {((item.key === 'overview' && overviewHasDraft) || (item.key === 'appendices' && appendicesHasDraft) || (item.key === 'crosswalks' && crosswalksHasDraft)) && (
                  <span
                    className="tree-draft-badge sidebar-nav-draft-badge"
                    title={t('draftPreview.itemBadgeAdditionalReading')}
                    aria-label={t('draftPreview.itemBadgeAdditionalReading')}
                  >
                    {t('sidebar.draftBadgeLabel')}
                  </span>
                )}
              </div>
            )
            return isCrosswalksOffline ? (
              <Tooltip key={item.key} title={t('crosswalks.offlineUnavailable')} placement="right">
                {navDiv}
              </Tooltip>
            ) : navDiv
          })}
        </nav>
      </div>

      {!crosswalksAsReading && (
        <CrosswalksMenu
          open={crosswalksOpen}
          anchorEl={crosswalksAnchorRef.current}
          collection={crosswalksCollection}
          onClose={() => setCrosswalksOpen(false)}
        />
      )}

      <div className="sidebar-divider" role="separator" />

      {/* Collapsing is a desktop affordance only: the drawer is dismissed, not collapsed. */}
      {!isDrawer && (
        <Button
          className="sidebar-collapse-btn"
          variant="text"
          onClick={toggleSidebarCollapsed}
          aria-label={t('sidebar.collapseSidebar')}
          title={t('sidebar.collapseSidebar')}
          startIcon=<ChevronLeftIcon/>
          sx={{ color: 'var(--color-on-dark-70)', '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' } }}
        >
          {t('sidebar.collapseSidebarButton')}
        </Button>
      )}

      {!isDrawer && sidebarCollapsed &&
          <IconButton
            className="sidebar-expand-btn"
            onClick={toggleSidebarCollapsed}
            aria-label={t('sidebar.expandSidebar')}
            title={t('sidebar.expandSidebar')}
            sx={{ color: 'var(--color-on-dark-70)', '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-10)' } }}
          >
            <ChevronRightIcon />
        </IconButton>
      }

      <div className="sidebar-controls" data-tour="sidebar-tree">
        <div className="sidebar-filter-row">
          <TextField
            className="sidebar-filter-input"
            placeholder={t('sidebar.filterPlaceholder')}
            value={filterInputValue}
            onChange={(e) => setFilterInputValue(e.target.value)}
            inputProps={{ 'aria-label': t('sidebar.filterPlaceholder') }}
            fullWidth
            slotProps={{
              input: {
                endAdornment: filterInputValue ? (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => { setFilterInputValue(''); setTreeFilterText('') }}
                      aria-label={t('sidebar.clearFilter')}
                      title={t('sidebar.clearFilter')}
                      edge="end"
                      size="small"
                      sx={{ color: 'var(--color-on-dark-70)', '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-12)' } }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'var(--color-white)',
                backgroundColor: 'var(--color-on-dark-08)',
                borderRadius: '4px',
                '& fieldset': { border: '1px solid var(--color-on-dark-20)' },
                '&:hover fieldset': { borderColor: 'var(--color-on-dark-40)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-on-dark-60)' },
              },
              '& input::placeholder': { color: 'var(--color-on-dark-40)', opacity: 1 },
            }}
          />
          <div className="sidebar-tree-btns">
            <IconButton
              title={t('sidebar.collapseAll')}
              aria-label={t('sidebar.collapseAll')}
              onClick={collapseAllPracticeAreas}
              size="small"
              sx={{ color: 'var(--color-on-dark-70)', '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-12)' } }}
            >
              <UnfoldLessIcon fontSize="small" />
            </IconButton>
            <IconButton
              title={t('sidebar.expandAll')}
              aria-label={t('sidebar.expandAll')}
              onClick={() => expandAllPracticeAreas(bundle?.practiceAreas.map((pa) => pa.id) ?? [])}
              size="small"
              sx={{ color: 'var(--color-on-dark-70)', '&:hover': { color: 'var(--color-white)', backgroundColor: 'var(--color-on-dark-12)' } }}
            >
              <UnfoldMoreIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
        {selectedLevelId !== null && bundle?.levels.find((l) => l.id === selectedLevelId)?.category === LevelType.Numeric && (
            <FormControlLabel
                className="sidebar-level-exact-label"
                title={t('sidebar.onlyThisLevel')}
                aria-label={t('sidebar.onlyThisLevel')}
                label={t('sidebar.onlyThisLevel')}
                sx={{ color: 'var(--color-on-dark-85)', '& .MuiFormControlLabel-label': { fontSize: '0.8rem' }, ml: 0 }}
                control={
                  <Checkbox
                      checked={levelFilterExact}
                      onChange={(e) => setLevelFilterExact(e.target.checked)}
                      sx={{
                        color: 'var(--color-on-dark-50)',
                        '&.Mui-checked': { color: 'var(--color-brand-accent)' },
                        '&:hover': { backgroundColor: 'var(--color-on-dark-10)' }
                      }}
                  />
                }
            />
        )}
      </div>

      <ContentTree />
    </>
  )
}
